import { useNavigate, useParams } from "react-router-dom";
import { DateTime } from "luxon";
import { Box, ListItemText, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import {
    KeyboardDoubleArrowDown as KeyboardDoubleArrowDownIcon,
    KeyboardDoubleArrowUp as KeyboardDoubleArrowUpIcon
} from "@mui/icons-material";
import { pink } from "@mui/material/colors";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";

import { useMSALPayload } from "@initia-digital/auth-client-utils";
import { baseDefinedAndNotNullNarrowing } from "@initia-digital/ts-utils";
import {
    BasicDatePicker, CheckboxListItemButton, ClickablePrestartListItem,
    FilledButton,
    IconButton, Textfield, Typography
} from "@initia-digital/component-library";
import { IPrestart } from "@initia-digital/types/work-and-safety";

import { SectionHeader } from "./SectionHeader";
import { ContinuingWorkAlert } from "./ContinuingWorkAlert";
import { useWorkAndSafetyStore } from "../store";
import { useFullTeamList, useIsAdmin } from "../hooks";
import { useQuery } from "@tanstack/react-query";
import { fetchPrestartList } from "../api/query";
import { sortPrestartByDate } from "../utils";



export const PrestartStart = () => {
    // ----------------------------------------
    const {
        editPrestartState, updateEditPrestartState
    } = useWorkAndSafetyStore();
    const {
        leadingTeam,
        prestartTimestamp,
        parentPrestartId, continuationDetail
    } = editPrestartState;

    let prestartLabel = 'Take-5';
    if (leadingTeam) {
        prestartLabel = 'Prestart meeting';
    }
    const continuingWork = baseDefinedAndNotNullNarrowing(parentPrestartId) && parentPrestartId !== '';

    const { jobId, planId, prestartId } = useParams();
    const isCreate = baseDefinedAndNotNullNarrowing(prestartId);

    const navigate = useNavigate();

    // MUI HOOKS
    const theme = useTheme();
    const isSmallerThanSm = useMediaQuery(theme.breakpoints.down('sm'));

    // AUTH AND DATA INTEGRATION
    // ---------------------
    const authDetail = useMSALPayload();
    const isAdmin = useIsAdmin(authDetail);

    const { fullTeamListData } = useFullTeamList(authDetail, true);

    
    // RECENT PRESTART LIST
    const { data: planYearPrestartsData } = useQuery({
        queryKey: ['prestartList', planId, DateTime.local().toFormat('yyyy')],
        queryFn: () => fetchPrestartList(authDetail, { planId, prestartDate: DateTime.local().toFormat('yyyy') }),
        enabled: baseDefinedAndNotNullNarrowing(planId)
    });
    let filteredPlanYearPrestarts: IPrestart[] = [];
    if (isCreate && baseDefinedAndNotNullNarrowing(planYearPrestartsData)) {
        // ----------------------------------------
        filteredPlanYearPrestarts = planYearPrestartsData.filter(prestart => (
            prestart.id !== prestartId &&
            prestart.planId === planId &&
            DateTime.fromISO(prestart.prestartTimestamp) <= DateTime.fromISO(prestartTimestamp)
        ))
        .sort(sortPrestartByDate).slice(0, 5);
    }
    else if (baseDefinedAndNotNullNarrowing(planYearPrestartsData)) {
        // --------------------------------------
        filteredPlanYearPrestarts = planYearPrestartsData.filter(prestart => DateTime.fromISO(prestart.prestartTimestamp) <= DateTime.fromISO(prestartTimestamp))
            .filter(prestart => prestart.id !== prestartId).sort(sortPrestartByDate).slice(0, 5);
    }

    return (
        <Box
            id="prestart-start"
            display="flex" flexDirection="column"
            border={isSmallerThanSm ? "none" : "1px solid lightgrey"}
            borderTop={`1px solid ${theme.palette.secondary.main}`} borderRadius={isSmallerThanSm ? "none" : "5px"}
            pb="20px" px={isSmallerThanSm ? '10px' : '20px'}
            mt={isSmallerThanSm ? '20px' : '0px'} pt={isSmallerThanSm ? '20px' : '0px'}
        >
            <Box display="flex" flexDirection="row" alignItems="center">
                <SectionHeader label="1. Start" />
                <Box display="flex" flexDirection="row" justifyContent="flex-end">
                    <Box mr="10px">
                        <IconButton
                            size="vsmall"
                            onClick={() => window.location.hash = '#review-the-plans'}
                            tooltipTitle={`Go to Review the Plans`}
                            color={theme.palette.secondary.contrastText}
                            backgroundColor={theme.palette.secondary.main}
                            hoverColor={theme.palette.primary.main}
                            hoverBackgroundColor={theme.palette.primary.contrastText}
                        >
                            <KeyboardDoubleArrowDownIcon />
                        </IconButton>
                    </Box>
                    <Box mr="10px">
                        <IconButton
                            size="vsmall"
                            onClick={() => window.location.hash = '#closure-and-contacts'}
                            tooltipTitle={`Go to Closure and Contacts`}
                            color={theme.palette.secondary.contrastText}
                            backgroundColor={theme.palette.secondary.main}
                            hoverColor={theme.palette.primary.main}
                            hoverBackgroundColor={theme.palette.primary.contrastText}
                        >
                            <KeyboardDoubleArrowUpIcon />
                        </IconButton>
                    </Box>
                </Box>
            </Box>
            <Box mb="4px">
                <LocalizationProvider dateAdapter={AdapterLuxon}>
                    <BasicDatePicker
                        value={baseDefinedAndNotNullNarrowing(prestartTimestamp) ? DateTime.fromISO(prestartTimestamp) : null}
                        name="prestartTimestamp"
                        label={`When is this ${prestartLabel} for?`}
                        handleChange={(value) => {
                            // ---------------------
                            updateEditPrestartState({
                                ...editPrestartState,
                                prestartTimestamp: value?.toISO() ?? DateTime.local().toISO()
                            });
                        }}
                    />
                </LocalizationProvider>
            </Box>
            <Box my="4px">
                <Typography
                    fontSize="0.9rem"
                    fontWeight={700}
                    color={theme.palette.primary.contrastText}
                >
                    Has work already started under this plan?
                </Typography>
                {
                    baseDefinedAndNotNullNarrowing(filteredPlanYearPrestarts) && filteredPlanYearPrestarts.length === 0 && (
                        <Tooltip
                            title="Once you have created a Take-5 or prestart meeting you will be able to reference it for future prestarts or Take-5's"
                            placement="top-end"
                        >
                            <Box>
                                <Typography
                                    fontSize="0.8rem"
                                    fontWeight={300}
                                    color={theme.palette.primary.contrastText}
                                >
                                    There have not been any recent prestarts/Take-5's under this plan.
                                </Typography>
                            </Box>
                        </Tooltip>
                    )
                }
                {
                    baseDefinedAndNotNullNarrowing(filteredPlanYearPrestarts) && filteredPlanYearPrestarts.length > 0 && (
                        <Box
                            display="flex" flexDirection="column" rowGap="4px"
                        >
                            <Typography
                                fontSize="0.8rem"
                                fontWeight={300}
                                color={theme.palette.primary.contrastText}
                            >
                                To continue from an existing prestart or Take-5, please select one below
                            </Typography>
                            <Box
                                display="flex" flexDirection="column" rowGap="8px"
                            >
                                {
                                    filteredPlanYearPrestarts.map(prestart => (
                                        <ClickablePrestartListItem
                                            key={`${prestart.id}-prestart-list-item`}
                                            dense
                                            prestart={prestart}
                                            isAdmin={isAdmin}
                                            teamMemberList={fullTeamListData}
                                            emphasized={prestart.id === parentPrestartId}
                                            onClick={() => {
                                                updateEditPrestartState({
                                                    ...editPrestartState,
                                                    parentPrestartId: (prestart.id === parentPrestartId) ? '' : prestart.id
                                                });
                                            }}
                                        />
                                    ))
                                }
                            </Box>
                        </Box>
                    )
                }
                {
                    continuingWork && (
                        <ContinuingWorkAlert />
                    )
                }
                {
                    continuingWork && (
                        <Box
                            display="flex"
                        >
                            <FilledButton
                                key={`view-linked-prestart-${parentPrestartId}`}
                                size="vsmall"
                                tooltipTitle="View prestart"
                                onClick={() => {
                                    // ---------------------
                                    navigate(`/job/${jobId}/plan/${planId}/view-prestart/${parentPrestartId}`);
                                    window.scrollTo(0, 0);
                                }}
                            >
                                View Prestart
                            </FilledButton>
                        </Box>
                    )
                }
                {
                    continuingWork && (
                        <Box>
                            <Textfield
                                fontSize="0.8rem"
                                value={continuationDetail}
                                name="continuationDetail"
                                label="Changes that affect today's work"
                                minRows={3}
                                placeholder="Describe any changes"
                                handleChange={(event) => {
                                    // ---------------------
                                    updateEditPrestartState({
                                        ...editPrestartState,
                                        continuationDetail: event.target.value
                                    });
                                }}
                            />
                        </Box>
                    )
                }
            </Box>
            <Box mt="4px">
                <Typography
                    fontSize="0.9rem"
                    fontWeight={700}
                    color={theme.palette.primary.contrastText}
                >
                    Prestart meeting or Take-5?
                </Typography>
                <CheckboxListItemButton
                    isSelected={leadingTeam}
                    onSelect={() => {
                        // ---------------------
                        updateEditPrestartState({
                            ...editPrestartState,
                            leadingTeam: !leadingTeam
                        });
                    }}
                    label={
                        <ListItemText>
                            <Typography
                                fontSize={"0.9rem"}
                                fontWeight={300}
                                color={theme.palette.primary.contrastText}
                            >
                                Are we the head contractor or running a prestart meeting?
                            </Typography>
                        </ListItemText>
                    }
                />
                <Box>
                    <Typography
                        fontSize="0.8rem"
                        fontWeight={300}
                        color={pink[500]}
                    >
                        {`You have chosen to ${leadingTeam ? 'run a prestart meeting' : 'complete a Take-5'}`}
                    </Typography>
                </Box>
            </Box>
        </Box>
    )
}